import React from 'react';
import Seo from '@/components/seo/Seo';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaTrash, FaImage, FaArrowLeft } from 'react-icons/fa';
import { useCart } from '@/context/CartContext';
import { formatPrice } from '@/lib/format';
import { mediaUrl } from '@/lib/media';
import { EmptyState } from '@/modules/shop/components/ShopStates';

/** Korpa se čita iz `CartContext` (localStorage) — nema poziva ka backendu. */
const CartPage = () => {
  const { items, total, updateQuantity, removeItem } = useCart();

  return (
    <>
      {/* Korpa je privatna za korisnika — nema šta da se indeksira. */}
      <Seo title="Korpa" noindex />

      <div className="pt-32 pb-20 min-h-[60vh]" data-testid="cart-page">
        <div className="container-custom">
          <h1 className="font-heading text-4xl text-brand-primary mb-10">Vaša korpa</h1>

          {items.length === 0 ? (
            <EmptyState
              title="Korpa je prazna"
              description="Dodajte proizvode iz prodavnice pa se vratite ovde."
              actionTo="/shop"
              actionLabel="Idite u prodavnicu"
            />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              <div className="lg:col-span-2 space-y-6">
                {items.map((item) => (
                  <motion.div
                    key={`${item.productId}-${item.variantId ?? 'base'}`}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex gap-5 border-b border-neutral-200 pb-6"
                    data-testid={`cart-item-${item.productId}-${item.variantId ?? 'base'}`}
                  >
                    <div className="w-24 h-24 bg-neutral-50 flex-shrink-0 overflow-hidden">
                      {item.imageUrl ? (
                        <img src={mediaUrl(item.imageUrl)} alt={item.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-neutral-200">
                          <FaImage size={24} />
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <h2 className="font-heading text-lg text-brand-primary mb-1">
                        {item.slug ? (
                          <Link to={`/proizvod/${item.slug}`} className="hover:text-brand-accent transition-colors">
                            {item.name}
                          </Link>
                        ) : (
                          item.name
                        )}
                      </h2>
                      <p className="text-sm text-neutral-500 mb-3">{formatPrice(item.price)} po komadu</p>

                      <div className="flex items-center gap-4">
                        <div className="flex items-center border border-neutral-200 rounded-sm">
                          <button
                            type="button"
                            onClick={() => updateQuantity(item.productId, item.variantId, item.quantity - 1)}
                            className="px-3 py-1 text-brand-primary hover:text-brand-accent transition-colors"
                            aria-label="Smanji količinu"
                          >
                            −
                          </button>
                          <span className="px-3 py-1 min-w-[2.5rem] text-center">{item.quantity}</span>
                          <button
                            type="button"
                            onClick={() => updateQuantity(item.productId, item.variantId, item.quantity + 1)}
                            className="px-3 py-1 text-brand-primary hover:text-brand-accent transition-colors"
                            aria-label="Povećaj količinu"
                          >
                            +
                          </button>
                        </div>

                        <button
                          type="button"
                          onClick={() => removeItem(item.productId, item.variantId)}
                          className="text-neutral-500 hover:text-error transition-colors p-2"
                          aria-label="Ukloni iz korpe"
                          data-testid="cart-remove"
                        >
                          <FaTrash size={14} />
                        </button>
                      </div>
                    </div>

                    <div className="text-right font-medium text-brand-primary whitespace-nowrap">
                      {formatPrice(item.price * item.quantity)}
                    </div>
                  </motion.div>
                ))}

                <Link
                  to="/shop"
                  className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-brand-accent transition-colors"
                >
                  <FaArrowLeft size={12} />
                  Nastavite kupovinu
                </Link>
              </div>

              {/* Rekapitulacija */}
              <div className="lg:col-span-1">
                <div className="bg-neutral-50 p-8 sticky top-28">
                  <h2 className="font-heading text-xl text-brand-primary mb-6">Pregled porudžbine</h2>

                  <div className="flex justify-between text-neutral-700 mb-3">
                    <span>Međuzbir</span>
                    <span data-testid="cart-subtotal">{formatPrice(total)}</span>
                  </div>
                  <div className="flex justify-between text-neutral-500 text-sm mb-6">
                    <span>Dostava</span>
                    <span>obračunava se u sledećem koraku</span>
                  </div>

                  <Link to="/checkout" className="btn-accent w-full text-center block" data-testid="go-to-checkout">
                    Nastavi na poručivanje
                  </Link>

                  <p className="text-xs text-neutral-500 mt-4 text-center">
                    Plaćanje pouzećem, bez online plaćanja karticom.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CartPage;
